import React from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
} from '@material-ui/core';

interface FeatureWithLimit {
  id: number;
  name: string;
  max_limit?: number;
}

interface Feature {
  id: number;
  name: string;
  config: any;
  isvisible: boolean;
}

interface FeaturesListSectionProps {
  filteredFeatures: Feature[];
  selectedFeatures: number[];
  featureLimits: { [key: number]: number };
  activeFeatures: FeatureWithLimit[];
  isSubmitting: boolean;
  onFeatureToggle: (featureId: number) => void;
  onLimitChange: (featureId: number, limit: number) => void;
  featuresWithLimits?: number[];
}

const FeaturesListSection: React.FC<FeaturesListSectionProps> = ({
  filteredFeatures,
  selectedFeatures,
  featureLimits,
  activeFeatures,
  isSubmitting,
  onFeatureToggle,
  onLimitChange,
  featuresWithLimits,
}) => {
  // Get the max_limit for a feature from activeFeatures
  const getFeatureLimit = (featureId: number): number | undefined => {
    const activeFeature = activeFeatures.find((f) => f.id === featureId);
    return activeFeature?.max_limit;
  };

  // Check if feature has a limit configuration
  const hasLimitConfig = (featureId: number): boolean => {
    // If featuresWithLimits is provided (from AddPlanModal), use that
    if (featuresWithLimits) {
      return featuresWithLimits.includes(featureId);
    }
    // Otherwise, check activeFeatures (for ActiveFeaturesModal)
    return getFeatureLimit(featureId) !== undefined;
  };

  return (
    <Box className="featuresListWrapper">
      {filteredFeatures.length > 0 ? (
        filteredFeatures.map((feature) => {
          const isSelected = selectedFeatures.includes(feature.id);
          const hasLimit = hasLimitConfig(feature.id);
          const currentLimit =
            featureLimits[feature.id] || getFeatureLimit(feature.id) || '';

          return (
            <Box
              key={feature.id}
              className={`featureItemCard ${
                isSelected ? 'featureItemCardSelected' : ''
              } ${isSubmitting ? 'featureItemCardDisabled' : ''}`}
              style={{
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.6 : 1,
              }}
            >
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSelected}
                      onChange={() => onFeatureToggle(feature.id)}
                      color="primary"
                      className="featureCheckboxInput"
                      disabled={isSubmitting}
                    />
                  }
                  label={
                    <Box className="featureContent">
                      <Typography variant="body2" className="featureNameText">
                        {feature.name}
                      </Typography>
                    </Box>
                  }
                  className="featureFormControl"
                  style={{ flex: 1, margin: 0 }}
                  // onClick={(e) => {
                  //   e.stopPropagation();
                  //   if (!isSubmitting) {
                  //     onFeatureToggle(feature.id);
                  //   }
                  // }}
                />

                {hasLimit && (
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginLeft: '16px',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Typography
                      variant="caption"
                      style={{ fontSize: '12px', color: '#666' }}
                    >
                      Limit:
                    </Typography>
                    <TextField
                      type="number"
                      value={currentLimit}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        onLimitChange(feature.id, value);
                      }}
                      variant="outlined"
                      size="small"
                      disabled={isSubmitting}
                      style={{ width: '80px' }}
                      inputProps={{
                        min: 0,
                        style: { fontSize: '13px', padding: '6px 8px' },
                      }}
                      InputProps={{
                        style: { fontSize: '13px' },
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          );
        })
      ) : (
        <Box className="featuresEmptyState">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="40"
            height="40"
            fill="currentColor"
            viewBox="0 0 16 16"
          >
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
            <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
          </svg>
          <Typography variant="body2" className="featuresEmptyText">
            No features found
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default FeaturesListSection;
